import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Icon } from 'react-bulma-components';
import { FaSliders } from 'react-icons/fa6';
import Checkbox from '../../ui/checkbox/checkbox';
import FilterBarItem from '../filter-bar-item/filter-bar-item';
import styles from './customize-view-menu.module.scss';

export type CustomizeViewMenuOption = {
  title: string;
  applied: boolean;
  valueKey: string;
  checkedValue?: boolean | string;
  uncheckedValue?: boolean | string;
};

export type CustomizeViewMenuProps<T> = {
  options: CustomizeViewMenuOption[];
  localData: T;
  setLocalData: Dispatch<SetStateAction<T>>;
  onSubmit: (values: T) => void;
  onClear: () => void;
};

export function CustomizeViewMenu<T>({
  options,
  localData,
  setLocalData,
  onSubmit,
  onClear,
}: CustomizeViewMenuProps<T>) {
  const appliedViewCustomizations = useMemo(
    () => options.filter((option) => option.applied).length,
    [options]
  );

  const resolveCheckedValue = useCallback(
    (option: CustomizeViewMenuOption) => {
      const optionValue = localData[option.valueKey as keyof T];
      if (option.checkedValue) {
        return optionValue === option.checkedValue;
      }
      return !!optionValue;
    },
    [localData]
  );

  const handleOptionClick = useCallback(
    (option: CustomizeViewMenuOption) => {
      setLocalData((prevData) => {
        const isChecked = resolveCheckedValue(option);
        if (isChecked) {
          return {
            ...prevData,
            [option.valueKey]: option.uncheckedValue ?? false,
          };
        } else {
          return {
            ...prevData,
            [option.valueKey]: option.checkedValue ?? true,
          };
        }
      });
    },
    [resolveCheckedValue, setLocalData]
  );

  const handleSubmit = useCallback(() => {
    onSubmit(localData);
  }, [localData, onSubmit]);

  const handleClear = useCallback(() => {
    onClear();
  }, [onClear]);

  return (
    <FilterBarItem
      title="Show/hide"
      appliedCount={appliedViewCustomizations}
      onClear={handleClear}
      onSubmit={handleSubmit}
      icon={
        <Icon color="primary-dark" className={styles.menuIcon}>
          <FaSliders />
        </Icon>
      }
    >
      <div className={styles.scrollContainer}>
        {options.map((option) => {
          return (
            <div
              key={`${option.valueKey}`}
              className={styles.option}
              onClick={() => handleOptionClick(option)}
            >
              <Checkbox
                className={styles.checkbox}
                checked={resolveCheckedValue(option)}
                aria-label={option.title}
                readOnly
              />
              <span className="is-size-6">{option.title}</span>
            </div>
          );
        })}
      </div>
    </FilterBarItem>
  );
}

export default CustomizeViewMenu;
